// ✅ COMPONENTE React (TCITestForm.jsx)
import React, { useState, useEffect } from 'react';
import { useMutation, gql } from '@apollo/client';
import './TCITestForm.css';

const GENERAR_PERFIL_TCI = gql`
  mutation GenerarPerfilTCI_User(
    $usernameAut: String!
    $respuestas: [Int!]!
    $primenombre: String!
    $apellido: String!
    $fecha: String!
    $useremail: String!
  ) {
    generarPerfilTCI_User(
      usernameAut: $usernameAut
      respuestas: $respuestas
      primenombre: $primenombre
      apellido: $apellido
      fecha: $fecha
      useremail: $useremail
    ) 
  }
`;


const preguntasOriginales = [
  { texto: 'Me gusta probar cosas nuevas incluso si implican algún riesgo.', dimension: 'NS' },
  { texto: 'Me aburro fácilmente cuando hago las mismas actividades durante mucho tiempo.', dimension: 'NS' },
  { texto: 'Me preocupo con frecuencia por lo que podría salir mal.', dimension: 'HA' },
  { texto: 'Me siento incómodo en situaciones nuevas o desconocidas.', dimension: 'HA' },
  { texto: 'Necesito sentir que los demás me valoran para sentirme bien conmigo mismo.', dimension: 'RD' },
  { texto: 'A menudo busco aprobación cuando tomo decisiones importantes.', dimension: 'RD' },
  { texto: 'Aunque algo sea muy difícil, sigo insistiendo hasta lograrlo.', dimension: 'P' },
  { texto: 'Me esfuerzo al máximo incluso cuando los resultados tardan en llegar.', dimension: 'P' },
  { texto: 'Soy capaz de mantenerme firme en mis decisiones incluso cuando enfrento obstáculos.', dimension: 'SD' },
  { texto: 'Me siento responsable de mis propias acciones y elecciones.', dimension: 'SD' },
  { texto: 'Trato de comprender a las personas incluso cuando no estoy de acuerdo con ellas.', dimension: 'C' },
  { texto: 'Me esfuerzo por ser amable y justo con los demás.', dimension: 'C' },
  { texto: 'A menudo siento que hay una conexión entre todos los seres vivos.', dimension: 'ST' },
  { texto: 'Me considero una persona espiritual, aunque no necesariamente religiosa.', dimension: 'ST' }
];

function TCITestForm({ username, nombre, apellido, fecha, email, refetch, refetchUser }) {
  const [preguntas, setPreguntas] = useState([]);
  const [respuestas, setRespuestas] = useState(Array(preguntasOriginales.length).fill(null));
  const [mostrarResultado, setMostrarResultado] = useState(false);
  const [generarPerfil, { data, loading, error }] = useMutation(GENERAR_PERFIL_TCI);

  useEffect(() => {
    const mezcladas = [...preguntasOriginales]
      .sort(() => Math.random() - 0.5)
      .reduce((acc, actual) => {
        const insertPos = acc.findIndex(p => p.dimension === actual.dimension);
        if (insertPos === -1) return [...acc, actual];
        const mitad = Math.floor(acc.length / 2);
        acc.splice(mitad, 0, actual);
        return acc;
      }, []);
    setPreguntas(mezcladas);
  }, []);

  const handleChange = (index, value) => {
    const nuevas = [...respuestas];
    nuevas[index] = parseInt(value, 10);
    setRespuestas(nuevas);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
  
    // Reordenar respuestas por dimensión
    const respuestasConDimensiones = preguntas.map((p, idx) => ({
      dimension: p.dimension,
      valor: respuestas[idx]
    }));
    const ordenDimensiones = ['NS', 'HA', 'RD', 'P', 'SD', 'C', 'ST'];
    const respuestasOrdenadas = ordenDimensiones.flatMap(dim =>
      respuestasConDimensiones
        .filter(r => r.dimension === dim)
        .map(r => r.valor)
    );
  
    // Formatear fecha "YYYY-MM-DD" → "DDMMAAAA"
    const [yyyy, mm, dd] = fecha.split('-');
    const fechaFormateada = `${dd}${mm}${yyyy}`;
  
    // 1) Lanza la mutación para generar el perfil TCI
    await generarPerfil({
      variables: {
        usernameAut: username,
        respuestas: respuestasOrdenadas,
        primenombre: nombre,
        apellido,
        useremail: email,
        fecha: fechaFormateada
      }
    });
  
    // 2) Refetch de userConductualData para obtener el usuario creado
    await refetchUser();
  
    // 3) Refetch de userConductualLinkData hasta que ya no venga vacío
    let linkResult;
    do {
      linkResult = await refetch();
      const links = linkResult.data?.getUsersLinkByUsername;
      if (!links || links.length === 0) {
        // Esperar 1 segundo antes de intentar de nuevo
        await new Promise(r => setTimeout(r, 1000));
      }
    } while (!linkResult.data?.getUsersLinkByUsername?.length);
  
    // 4) Mostrar el resultado y permitir cerrar el test
    setMostrarResultado(true);
  };
  

  const formularioCompleto = respuestas.every(r => typeof r === 'number');

  const reiniciarTest = () => {
    const mezcladas = [...preguntasOriginales]
      .sort(() => Math.random() - 0.5)
      .reduce((acc, actual) => {
        const insertPos = acc.findIndex(p => p.dimension === actual.dimension);
        if (insertPos === -1) return [...acc, actual];
        const mitad = Math.floor(acc.length / 2);
        acc.splice(mitad, 0, actual);
        return acc;
      }, []);
    setPreguntas(mezcladas);
    setRespuestas(Array(preguntasOriginales.length).fill(null));
    setMostrarResultado(false);
  };

  return (
    <div className="tci-container">
      <h5 className="tci-title">Instrucciones del Test TCI</h5>
      <ul className="tci-instructions">
        <li>Responde usando una escala de 1 a 5:</li>
        <li><strong>1</strong>: No estoy de acuerdo</li>
        <li><strong>2</strong>: Poco en desacuerdo</li>
        <li><strong>3</strong>: Neutral</li>
        <li><strong>4</strong>: Poco de acuerdo</li>
        <li><strong>5</strong>: Completamente de acuerdo</li>
      </ul>

      <button onClick={reiniciarTest} className="tci-button" style={{ marginBottom: '1rem' }}>
        Reiniciar Test
      </button>

      <div className="tci-scroll">
        <form onSubmit={handleSubmit} className="tci-form">
          {preguntas.map((pregunta, idx) => {
            const incompleta =
              respuestas.some((r, i) => i > idx && r !== null) &&
              respuestas[idx] === null;
            return (
              <div
                key={idx}
                className={`tci-question-block ${incompleta ? 'tci-incompleta' : ''}`}
              >
                <label className="tci-label">{pregunta.texto}</label>
                <div className="tci-radio-group">
                  {[1, 2, 3, 4, 5].map(val => (
                    <label key={val} className="tci-radio-option">
                      <input
                        type="radio"
                        name={`pregunta-${idx}`}
                        value={val}
                        checked={respuestas[idx] === val}
                        onChange={() => handleChange(idx, val)}
                      />
                      <span>{val}</span>
                    </label>
                  ))}
                </div>
              </div>
            );
          })}

          <div style={{ paddingBottom: '2rem' }}>
            <button type="submit" className="tci-button" disabled={!formularioCompleto}>
              Generar Perfil
            </button>
          </div>
        </form>
      </div>

      {loading && <p className="tci-loading">Generando perfil...</p>}
      {error && <p className="tci-error">Error: {error.message}</p>}

      {data && mostrarResultado && (
       <div className="loading-text">
       <p>Gracias por completar el test TCI, Puedes cerrar la pestaña del navegador!</p>
     </div>
      )}
    </div>
  );
}

export default TCITestForm;
